import { useCountry } from 'hooks/useCountry'
import { useQuery } from 'react-query'
import { AppointmentTypes } from '../../types'
import { logFactory } from '../../util/logFactory'
import { useAppointmentsService } from '../services'
import { useAppointmentLocationState } from '../useAppointmentLocationState'
import { useSearchParameters } from '../useSearchParameters'
import { useShop } from 'hooks'

interface UseAppointmentTypesContext {
  appointmentTypes?: AppointmentTypes
  isError: boolean
  isLoading: boolean
}

const hookName = 'useAppointmentTypes'
const log = logFactory(hookName)

const useAppointmentTypes = (): UseAppointmentTypesContext => {
  const paramCountry = useCountry()
  const { journey: searchJourney } = useSearchParameters()
  const { shop, isLoading: isShopLoading } = useShop()
  const { country: appointmentStateCountry, journey: appointmentStateJourney } = useAppointmentLocationState()

  const country = (paramCountry ?? appointmentStateCountry ?? '').toString().toUpperCase()
  const journey = (searchJourney ?? appointmentStateJourney ?? '').toString().toUpperCase()
  const shopType = shop?.type

  const params = { country, journey, shopType }

  const { getAppointmentTypes, isReady } = useAppointmentsService()

  const fetchShop = async (): Promise<AppointmentTypes> => {
    log(`🗓️ Fetching appointment types...`, params)

    const found = await getAppointmentTypes(params)

    log(`🗓️ Found appointment types`, found)

    return found
  }

  const isQueryEnabled = Boolean(country && journey && isReady && !isShopLoading)

  const {
    data: appointmentTypes,
    isError,
    isLoading,
  } = useQuery([hookName, params], fetchShop, {
    enabled: isQueryEnabled,
  })

  return {
    appointmentTypes,
    isError,
    isLoading: (!isReady || isLoading || isShopLoading) && !appointmentTypes,
  }
}

export { useAppointmentTypes }
